import React from 'react';
import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import { getQrCodesForIdsWithUrl } from './QRCodeGenerator';

export async function createQRStickersPDF(
  gridParameters,
  selectedItems,
  labels,
  urlTemplateFunction,
  downloadFileName,
  errorHandler
) {
  const qrCodes = await getQrCodesForIdsWithUrl(
    selectedItems,
    urlTemplateFunction,
    errorHandler
  );
  buildPDFDocument(gridParameters, qrCodes, labels, downloadFileName);
}

function setupPdfDocumentProperties(grid) {
  const doc = new jsPDF({
    orientation: grid.pdfProperties.orientation,
    unit: grid.pdfProperties.unit,
    format: grid.pdfProperties.format
  });
  doc.setFontSize(grid.size.font);
  return doc;
}

function prepareStickersFromQRsAndLabels(emptyStickersNumber, qrs, labels) {
  const stickers = Object.keys(qrs).map(id => ({ label: labels[id], qr: qrs[id] }))

  // User passes number of already used stickers in the printing paper, when creating pdf we just skip nulls
  const emptyStickers = new Array(emptyStickersNumber).fill(null);
  const allStickers = emptyStickers.concat(stickers);
  return allStickers;
}

function iterateThroughGridForSinglePage(processFunction, doc, grid, stickersSlice) {
  for (let row = 0; row < grid.rows; row++) {
    for (let col = 0; col < grid.cols; col++) {
      processFunction(row, col, doc, grid, stickersSlice);
    }
  }
}

function addSingleStickerToDoc(doc, qr, label, startX, startY, grid) {

  function addLabelToSticker() {
    const labelStartX =
      startX +
      grid.pad.internal.qrLeft +
      grid.size.qr +
      grid.pad.internal.qrRight +
      grid.pad.internal.label;
    const labelStartY = startY + grid.pad.internal.labelTop + grid.size.font / 2;
    const wrappedLabel = doc.splitTextToSize(
      label.substring(0, 35).replace(/\n|\r/g, ' ') +
      `${label.length > 35 ? '...' : ''}`,
      grid.size.labelH
    );
    doc.text(wrappedLabel, labelStartX, labelStartY);
  }

  addLabelToSticker();
  doc.addImage(
    qr,
    startX + grid.pad.internal.qrLeft,
    startY + grid.pad.internal.qrTop,
    grid.size.qr,
    grid.size.qr
  );
}

function processSingleGridCell(row, col, doc, grid, stickersSlice) {
  const stickerNumber = grid.cols * row + col; // Map sticker index from 2 dimensions into 1
  if (stickerNumber < stickersSlice.length) {
    // Generate only required number of stickers
    const sticker = stickersSlice[stickerNumber];
    if (sticker != null) {
      // Skip empty stickers
      const stickerStartX =
        grid.margin.left + (grid.getStickerTotalXSize + grid.pad.external.horizontal) * col;
      const stickerStartY =
        grid.margin.top + (grid.getStickerTotalYSize + grid.pad.external.vertical) * row;
      addSingleStickerToDoc(
        doc,
        sticker.qr,
        sticker.label,
        stickerStartX,
        stickerStartY,
        grid
      );
    }
  }
}

function buildPDFDocument(grid, qrCodes, labels, downloadFileName) {
  const doc = setupPdfDocumentProperties(grid);
  const allStickers = prepareStickersFromQRsAndLabels(grid.emptyStickers, qrCodes, labels);

  // Multi-page pdf, if there is more elements than stickers on single grid page
  const pagesRequired = Math.ceil(allStickers.length / (grid.cols * grid.rows));
  for (let pageNumber = 0; pageNumber < pagesRequired; pageNumber++) {
    const stickersSlice = allStickers.slice((grid.cols * grid.rows * pageNumber), ((grid.cols * grid.rows * (pageNumber + 1))));
    iterateThroughGridForSinglePage(processSingleGridCell, doc, grid, stickersSlice);
    if (pageNumber !== pagesRequired - 1) {
      doc.addPage('a4', 'p');
    }
  }
  doc.save(downloadFileName);
  return doc;
}

export function qrGenerationErrorHandler(t) {
  return () => {
    toast.error(t('toast.title.error.generate.qr'));
    return null;
  };
}
